/**
 * useSecurityCopilotFeatureData.hook.ts
 *
 * Custom React hook for fetching and managing feature data for Security Copilot customer view.
 *
 * Features:
 * - Accepts optional tenantId to filter data.
 * - Uses a toggleable fetch trigger (shouldFetch) to control when data is fetched.
 * - Fetches feature data using a Redux Toolkit query hook (useGetFeatureDataForSecurityCopilotQuery).
 * - Handles loading, error, and data states.
 * - Sorts the fetched data.
 * - Extracts and sets all feature names for selection.
 * - Avoids unnecessary state updates by comparing previous and new values.
 * - Logs telemetry events for fetch triggers, errors, successes, and feature selection changes.
 * - Exposes:
 *   - error: error state from the query.
 *   - isLoading: loading state from the query.
 *   - rawFeaturesData: the processed features data array.
 *   - selectedFeatures: array of selected feature names.
 *   - setSelectedFeatures: setter for selected features.
 *   - triggerFetch: function to manually trigger data fetch.
 */
import { useCallback, useEffect, useState } from "react";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { IArrayKey } from "../../../../interfaces/CarouselProps.interfaces";
import { useGetFeatureDataForSecurityCopilotQuery } from "../../../../slices/ExploreCustomers.slice";
import { arrSortDescByKeyLength } from "../../../../Utils/js-common";
import { FeaturesData } from "../ExploreFeatures.interfaces";

export const useSecurityCopilotFeatureData = (
  tenantId?: string,
  aggregationTypes: string[] = ["Monthly", "Weekly", "Daily"]
) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const [rawFeaturesData, setRawFeaturesData] = useState<FeaturesData[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

  const { data, isLoading, error } = useGetFeatureDataForSecurityCopilotQuery({ id: tenantId }, { skip: !shouldFetch });

  const triggerFetch = useCallback(() => {
    setShouldFetch((prev) => !prev);
    logEvent("Product Telemetry", "Customer Page", "ExploreFeatures_useSecurityCopilotFeatureData", {
      eventName: "FeatureData_FetchTriggered",
      hookName: "useSecurityCopilotFeatureData",
      tenantId
    });
  }, [tenantId]);

  useEffect(() => {
    if (error) {
      logException("Customer Page", "Product Telemetry", "useGetFeatureDataForSecurityCopilotQuery", error);
      logEvent("Product Telemetry", "Customer Page", "ExploreFeatures_useSecurityCopilotFeatureData", {
        eventName: "FeatureData_FetchError",
        hookName: "useSecurityCopilotFeatureData",
        tenantId,
        error: String(error)
      });
      return;
    }
    if (!data || data.length === 0) return;

    // Transform the API response to match FeaturesData structure
    // The API returns: [{ name: "ConsumedSCU", data: [{ name: "AllUp", data: [...snapshot data...] }] }]
    // FeaturesData expects: { name: string, data: Array<{ name: string, data: Array<{snapshotDate, currentValue}> }> }
    // We group by the feature name (AllUp, M365D, etc) and collect all their snapshot data
    // Filter to include specified aggregation types
    const featureMap = new Map<string, any[]>();

    data.forEach((item: any) => {
      if (item.data && Array.isArray(item.data)) {
        item.data.forEach((subItem: any) => {
          const featureName = subItem.name;
          if (!featureMap.has(featureName)) {
            featureMap.set(featureName, []);
          }
          // Filter by the specified aggregation types
          const filteredData = (subItem.data || []).filter((dataPoint: any) =>
            aggregationTypes.includes(dataPoint.aggregationType)
          );
          featureMap.get(featureName)!.push(...filteredData);
        });
      }
    });

    // Convert map to FeaturesData array
    // Create a single FeaturesData object that contains all features as FeatureData items
    const transformedData: FeaturesData[] = [];

    if (featureMap.size > 0) {
      const allFeatures: any[] = Array.from(featureMap).map(([featureName, snapshotData]) => ({
        name: featureName,
        data: snapshotData
      }));

      transformedData.push({
        name: "ConsumedSCU",
        data: allFeatures,
        kustoLink: ""
      });
    }

    // Deep clone and sort data
    const sortedData = arrSortDescByKeyLength({
      key: "data",
      data: JSON.parse(JSON.stringify(transformedData))
    } as IArrayKey);

    // Use all sorted data (no filtering like productName in the original hook)
    const features = [...sortedData];

    // Only update state if changed
    setRawFeaturesData((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(features)) {
        return features;
      }
      return prev;
    });

    const allFeatureNames = features.flatMap((feature) => (feature.data || []).map((f: any) => f.name));

    setSelectedFeatures((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(allFeatureNames)) {
        return allFeatureNames;
      }
      return prev;
    });

    logEvent("Product Telemetry", "Customer Page", "ExploreFeatures_useSecurityCopilotFeatureData", {
      eventName: "FeatureData_FetchSuccess",
      hookName: "useSecurityCopilotFeatureData",
      tenantId,
      featureCount: features.length,
      allFeatureNames
    });
  }, [data, error, tenantId]);

  // Log when selected features change
  useEffect(() => {
    logEvent("Product Telemetry", "Customer Page", "ExploreFeatures_useSecurityCopilotFeatureData", {
      eventName: "FeatureData_SelectedFeaturesChanged",
      hookName: "useSecurityCopilotFeatureData",
      tenantId,
      selectedFeatures
    });
  }, [selectedFeatures, tenantId]);

  return {
    error,
    isLoading,
    rawFeaturesData,
    selectedFeatures,
    setSelectedFeatures,
    triggerFetch
  };
};
